import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, Brain, FileQuestion, Bookmark, NotebookPen, Trophy, ScanLine,
  LifeBuoy, MessageCircle, Languages, Gamepad2, Blend, Coins, ShieldCheck,
  MessageCircleQuestion, Mic, ClipboardCheck, Flame, PencilRuler, Sigma,
  GitGraph, LineChart, Volume2, ChevronLeft, ChevronRight, Pause, Play,
  ArrowRight, Award, MessagesSquare, Youtube, Star, MessageSquare,
  Dumbbell, Calendar, TrendingUp,
} from "lucide-react";

/**
 * KeyFeatures — self-playing carousel of every shippable capability Oak AI
 * Campus offers. Re-designed Feb 2026 (was a 3×N grid) to sit side-by-side
 * with the welcome video on the dashboard and cut the dashboard's vertical
 * scroll height roughly in half — critical on mobile where the old grid
 * pushed the rest of the page below the fold.
 *
 * Each feature card is clickable — it deep-links to where the feature
 * actually lives so the carousel doubles as a navigation surface, not
 * just a static showcase.
 *
 * `cta` shapes:
 *   { to: "/route" }            → react-router navigation
 *   { event: "oak:open-…" }     → window.dispatchEvent (modal toggles)
 *   { scroll: "data-testid" }   → smooth-scrolls to that element on the
 *                                 current page (great for hub anchors)
 *
 * Iter 79 — surfaced the 2026 engagement pack (Boss Quiz, Mastery Seal,
 * Flashcards, Forum, Reviews, XP & Levels, Watch & Learn, Mini-games,
 * Feedback) at the TOP so they're the first thing returning users see.
 *
 * Iter 80 — exported the FEATURES array so the pre-login Landing page
 * can reuse the same single source of truth (rendered as a static
 * animated grid via `<LandingFeatureGrid />` instead of a carousel).
 */
export const FEATURES = [
  // --- 2026 Engagement Pack (top billing — newest shippables) ---------------
  { Icon: Award, title: "Boss Quiz + Mastery Seal",
    body: "Pass a 10-question final exam on any path to mint a permanent gold-foil Mastery Seal — animated certificate plus +100 XP and +3 credits.",
    accent: "#FFD700", testid: "feat-boss-quiz", badge: "NEW",
    cta: { to: "/library", label: "Earn a seal" } },
  { Icon: Brain, title: "Spaced-Repetition Flashcards",
    body: "Auto-generates a 5-card deck per lesson. Daily review surfaces what your brain needs next — +5 XP per card and a daily credit reward.",
    accent: "#9bc7ff", testid: "feat-flashcards", badge: "NEW",
    cta: { scroll: "hub-flashcards-section", label: "Open today's deck" } },
  { Icon: MessagesSquare, title: "Community Forum",
    body: "Per-course discussion threads. Ask questions, get answers from fellow learners. Moderated, profanity-scrubbed, beginner-friendly.",
    accent: "#bcb0ff", testid: "feat-forum", badge: "NEW",
    cta: { to: "/library", label: "Join a conversation" } },
  { Icon: Star, title: "Course Reviews + Ratings",
    body: "5-star ratings + written reviews on every path. Aggregate badges (★ 4.7 · 142 reviews) help others pick the best course for them.",
    accent: "#FFD700", testid: "feat-reviews", badge: "NEW",
    cta: { to: "/library", label: "Browse top-rated" } },
  { Icon: TrendingUp, title: "XP, Levels & Friend Ladder",
    body: "Every action earns XP. Climb 12 levels from Sprout to Master. Mutual referrals create a private leaderboard you can compete on.",
    accent: "#00FF9D", testid: "feat-xp", badge: "NEW",
    cta: { scroll: "hub-leaderboard-section", label: "See your rank" } },
  { Icon: Calendar, title: "Daily Streak + Freeze",
    body: "Calendar tracks every kept day. Two limitless freezes auto-save missed days so one busy Tuesday doesn't reset your streak.",
    accent: "#FF7B00", testid: "feat-streak-calendar", badge: "NEW",
    cta: { scroll: "hub-streak-section", label: "View your streak" } },
  { Icon: Youtube, title: "Watch & Learn (YouTube)",
    body: "Curated educational clips from Khan Academy, Veritasium, TED-Ed and more — surfaced beside every lesson, with floating PiP player.",
    accent: "#FF0000", testid: "feat-watch-learn", badge: "NEW",
    cta: { to: "/library", label: "Open a lesson" } },
  { Icon: Gamepad2, title: "In-Lesson Mini-Games",
    body: "Concept-match, fill-blank, order-it, true-false games triggered after Part 1 — earn +1 credit per game tied to the lesson you're reading.",
    accent: "#5D3FD3", testid: "feat-minigames", badge: "NEW",
    cta: { to: "/library", label: "Play a game" } },
  { Icon: MessageSquare, title: "Direct Feedback Channel",
    body: "Floating Feedback pill on every screen — bug, feature, complaint, content issue. Every message reaches a human, every reply.",
    accent: "#9bc7ff", testid: "feat-feedback", badge: "NEW",
    cta: { event: "oak:open-feedback", label: "Send feedback" } },

  // --- Core path generation -------------------------------------------------
  { Icon: Brain, title: "AI Mastery Paths",
    body: "Type any goal — Oak AI Campus builds a 5-step bento path with a 30-min whitepaper for each step.",
    accent: "#0044FF", testid: "feat-paths",
    cta: { scroll: "hub-search-input", label: "Generate a path" } },
  { Icon: MessageCircleQuestion, title: "Live Doubt Solver",
    body: "Sidebar AI tutor that already knows your exact whitepaper. 5 doubts per credit · free on Limitless.",
    accent: "#00FF9D", testid: "feat-doubt-solver",
    cta: { to: "/library", label: "Open a path" } },
  { Icon: Mic, title: "Voice Doubt Mode",
    body: "1-tap toggle. Speak your doubt aloud like Google Assistant — Oak transcribes and answers in real-time.",
    accent: "#00FF9D", testid: "feat-voice-doubt",
    cta: { to: "/library", label: "Try inside a path" } },

  // --- Interactive lesson surface ------------------------------------------
  { Icon: Volume2, title: "Live Classroom (Read Aloud)",
    body: "Sentence-chunked TTS with a warm female voice. 24+ languages. No more 15-second cutoffs.",
    accent: "#9bffcc", testid: "feat-readaloud",
    cta: { to: "/library", label: "Open a lesson" } },
  { Icon: PencilRuler, title: "Chalkboard Mode",
    body: "Watch Oak hand-write the key points paragraph-by-paragraph, synced perfectly to the narration.",
    accent: "#FFD700", testid: "feat-chalkboard",
    cta: { to: "/library", label: "Open a lesson" } },
  { Icon: ClipboardCheck, title: "Practice Quizzes",
    body: "Five MCQs after every lesson part. Score ≥ 60% to keep your reading streak alive.",
    accent: "#00FF9D", testid: "feat-quizzes",
    cta: { to: "/library", label: "Practice now" } },
  { Icon: Flame, title: "Reading Streaks",
    body: "Fire-pill in the header tracks daily reading. Hit 3 / 7 / 14 day milestones for bonus credits.",
    accent: "#FF7B00", testid: "feat-streak",
    cta: { event: "oak:open-ad", label: "Earn streak rewards" } },
  { Icon: Sigma, title: "KaTeX Math Rendering",
    body: "Drop LaTeX into any lesson — equations render inline with publication-quality typography.",
    accent: "#b9a8ff", testid: "feat-katex",
    cta: { to: "/library", label: "See in a lesson" } },
  { Icon: GitGraph, title: "Mermaid Diagrams",
    body: "Architecture flows, flowcharts, mind-maps — all rendered live inside the whitepaper.",
    accent: "#5D3FD3", testid: "feat-mermaid",
    cta: { to: "/library", label: "See in a lesson" } },
  { Icon: LineChart, title: "Live Charts",
    body: "Bar / line / area data viz powered by Recharts, embedded right in the lesson body.",
    accent: "#9bc7ff", testid: "feat-charts",
    cta: { to: "/library", label: "See in a lesson" } },

  // --- Capture & Recall ----------------------------------------------------
  { Icon: FileQuestion, title: "Adaptive Quick Exams",
    body: "10-question MCQ set per path; mastery levels feed into your shareable certificate.",
    accent: "#00FF9D", testid: "feat-exam",
    cta: { to: "/library", label: "Take an exam" } },
  { Icon: NotebookPen, title: "OAK-Pad",
    body: "Highlight any line — it auto-clips into a notebook you can export to PDF or publish.",
    accent: "#9bffcc", testid: "feat-oakpad",
    cta: { to: "/library", label: "Start clipping" } },
  { Icon: Sparkles, title: "Publish as Study Guide",
    body: "Turn your OAK-Pad clips into a public note with a cover image — a viral acquisition asset.",
    accent: "#9bffcc", testid: "feat-publish",
    cta: { to: "/library", label: "Open library" } },
  { Icon: Trophy, title: "Verifiable Certificates",
    body: "Foundation / Practitioner / Expert tiers. Public, shareable, SEO-indexed.",
    accent: "#FFD700", testid: "feat-certs",
    cta: { to: "/library", label: "View certificates" } },
  { Icon: Bookmark, title: "Library & Resume",
    body: "Save any path, auto-resume from your last scroll position, never lose progress.",
    accent: "#8aa8ff", testid: "feat-library",
    cta: { to: "/library", label: "Open library" } },

  // --- Multimodal & Modes ---------------------------------------------------
  { Icon: ScanLine, title: "OAK Vision",
    body: "Snap or upload a syllabus, slide deck or PDF — AI extracts a goal you can build a path from.",
    accent: "#5D3FD3", testid: "feat-vision",
    cta: { scroll: "hub-vision-section", label: "Try OAK Vision" } },
  { Icon: Languages, title: "Universal Language",
    body: "Paths in 24+ languages with native scripts — Hindi, Tamil, German, Spanish, more. No re-fetch drift.",
    accent: "#0044FF", testid: "feat-language",
    cta: { scroll: "lang-persona-help", label: "Pick a language" } },
  { Icon: Gamepad2, title: "Simulation Mode",
    body: "Practice scenarios as case studies — interactive forks, branching consequences, deeper recall.",
    accent: "#FF7B00", testid: "feat-sim",
    cta: { to: "/academy", label: "Open simulations" } },
  { Icon: Blend, title: "Cross-Discipline",
    body: "Blend two domains (e.g. design + economics) into a single mastery path that connects both.",
    accent: "#b9a8ff", testid: "feat-cross",
    cta: { to: "/academy", label: "Blend disciplines" } },

  // --- Account / Care -------------------------------------------------------
  { Icon: MessageCircle, title: "AI Counselor Chat",
    body: "Multi-turn coaching that helps you refine a vague goal before generating your path.",
    accent: "#8aa8ff", testid: "feat-counselor",
    cta: { scroll: "hub-search-input", label: "Chat with counselor" } },
  { Icon: LifeBuoy, title: "OAK Guide (Support)",
    body: "Ask anything about credits, features, or upgrades — free in-app AI assistant on every screen.",
    accent: "#00FF9D", testid: "feat-guide",
    cta: { event: "oak:open-guide", label: "Open OAK Guide" } },
  { Icon: Coins, title: "Earn Credits Hub",
    body: "Surveys, sponsor visits, referrals, streak milestones — top up your wallet without paying.",
    accent: "#FFD700", testid: "feat-earn",
    cta: { event: "oak:open-ad", label: "Earn credits" } },
  { Icon: Coins, title: "Razorpay & Limitless",
    body: "6 free on signup, 2 weekly top-up. Buy more or go Limitless (6-month unlimited) via Razorpay.",
    accent: "#FFD700", testid: "feat-credits",
    cta: { event: "oak:open-upgrade", label: "See plans" } },
  { Icon: ShieldCheck, title: "Care & Accessibility",
    body: "OpenDyslexic mode, Focus mode, Age-gate, and Micro-Chunker on every AI output — inclusive by default.",
    accent: "#9bffcc", testid: "feat-care",
    cta: { to: "/settings", label: "Open settings" } },
];

const AUTOPLAY_MS = 3500;

export default function KeyFeatures() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const total = FEATURES.length;

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(
      () => setIdx((i) => (i + 1) % total),
      AUTOPLAY_MS,
    );
    return () => clearInterval(timerRef.current);
  }, [paused, total]);

  // Honour the user's reduced-motion preference — disable autoplay entirely
  // (they can still drive the carousel via the dots / arrows).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) setPaused(true);
    const onChange = (e) => setPaused(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const goTo = (n) => setIdx(((n % total) + total) % total);
  const next = () => goTo(idx + 1);
  const prev = () => goTo(idx - 1);

  // Click handler — dispatches the right side-effect for whichever
  // `cta` shape the feature defined. Stops autoplay first so the user
  // doesn't fight a re-render mid-tap.
  const activateCTA = (cta) => {
    if (!cta) return;
    setPaused(true);
    if (cta.to) {
      navigate(cta.to);
    } else if (cta.event) {
      window.dispatchEvent(new Event(cta.event));
    } else if (cta.scroll) {
      const el = document.querySelector(`[data-testid="${cta.scroll}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        // Briefly focus inputs so the user can keep typing immediately.
        if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
          setTimeout(() => el.focus(), 350);
        }
      }
    }
  };

  const feature = FEATURES[idx];

  return (
    <section
      className="glass border border-white/10 rounded-2xl overflow-hidden flex flex-col h-full animate-fadeup"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      data-testid="hub-key-features"
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3 border-b border-white/5">
        <div className="label-eyebrow flex items-center gap-2 min-w-0">
          <span className="truncate">Everything OAK can do</span>
          <span
            className="text-[10px] font-display uppercase tracking-wider text-[#9bffcc] bg-[#00FF9D]/10 border border-[#00FF9D]/30 rounded-full px-2 py-0.5 shrink-0"
            data-testid="features-pill"
          >
            {total} features
          </span>
        </div>
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          className="h-7 w-7 grid place-items-center rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white/65 hover:text-white transition"
          aria-label={paused ? "Resume carousel" : "Pause carousel"}
          data-testid="btn-features-playpause"
          title={paused ? "Resume" : "Pause"}
        >
          {paused ? <Play size={11} /> : <Pause size={11} />}
        </button>
      </div>

      {/* Slide stage — fixed min-height so layout doesn't jump between
          short and long feature bodies. */}
      <div className="relative flex-1 min-h-[260px] md:min-h-[300px] grid">
        <FeatureSlide
          key={feature.testid}
          feature={feature}
          index={idx}
          onActivate={() => activateCTA(feature.cta)}
        />

        {/* Arrow controls — keep them tucked at the edges so they never
            cover the slide content. Hidden on the smallest screens to
            avoid crowding; users can still swipe-tap the dots. */}
        <button
          type="button"
          onClick={prev}
          aria-label="Previous feature"
          className="hidden sm:grid absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 place-items-center rounded-full border border-white/15 bg-[#0B0E14]/70 backdrop-blur text-white/65 hover:text-white hover:bg-[#0B0E14]/90 transition"
          data-testid="btn-features-prev"
        >
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="Next feature"
          className="hidden sm:grid absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 place-items-center rounded-full border border-white/15 bg-[#0B0E14]/70 backdrop-blur text-white/65 hover:text-white hover:bg-[#0B0E14]/90 transition"
          data-testid="btn-features-next"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Dots row — scrollable on narrow widths so 24 dots never wrap. */}
      <div
        className="px-3 pb-3 pt-2 border-t border-white/5 flex items-center gap-1.5 overflow-x-auto"
        style={{ scrollbarWidth: "none" }}
        data-testid="features-dots"
      >
        {FEATURES.map((f, i) => (
          <button
            key={f.testid}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Show feature ${i + 1}: ${f.title}`}
            className={`shrink-0 h-1.5 rounded-full transition-all ${
              i === idx
                ? "w-6 bg-white/85"
                : "w-1.5 bg-white/20 hover:bg-white/40"
            }`}
            data-testid={`features-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Single feature slide. Animates a soft fade-in every time its key prop
 * changes (the parent passes `key={feature.testid}`), so swapping slides
 * doesn't look static. The whole card is clickable — the parent figures
 * out where to send the user. We deliberately don't use a heavy library
 * here because the parent already controls timing + pause state.
 */
function FeatureSlide({ feature, index, onActivate }) {
  const { Icon, title, body, accent, testid, cta, badge } = feature;
  const ctaLabel = cta?.label || "Open";
  return (
    <button
      type="button"
      onClick={onActivate}
      className="text-left p-6 md:p-7 grid grid-rows-[auto_1fr_auto] gap-3 animate-fadeup hover:bg-white/[0.02] focus:bg-white/[0.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/20 transition group cursor-pointer"
      data-testid={testid}
      aria-label={`${title} — ${ctaLabel}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="rounded-xl p-2.5 shrink-0 border transition group-hover:scale-105"
          style={{ background: `${accent}1A`, borderColor: `${accent}55` }}
        >
          <Icon size={20} style={{ color: accent }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-[11px] uppercase tracking-[0.18em] text-white/45 flex items-center gap-2">
            Feature {index + 1}
            {badge && (
              <span
                className="text-[9px] font-mono-oak tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#00FF9D]/15 border border-[#00FF9D]/35 text-[#9bffcc]"
                data-testid={`${testid}-badge`}
              >
                {badge}
              </span>
            )}
          </div>
          <h3 className="font-display text-lg md:text-xl text-white tracking-tight leading-tight truncate">
            {title}
          </h3>
        </div>
      </div>
      <p className="text-white/65 text-sm leading-relaxed">
        {body}
      </p>
      <div
        className="inline-flex items-center gap-1.5 self-start text-[11px] font-display font-semibold uppercase tracking-wider rounded-full px-3 py-1.5 border transition group-hover:translate-x-0.5"
        style={{
          color: accent,
          background: `${accent}10`,
          borderColor: `${accent}40`,
        }}
        data-testid={`${testid}-cta`}
      >
        {ctaLabel}
        <ArrowRight size={11} />
      </div>
    </button>
  );
}
